import React, { useState } from 'react';
import { ChevronDown } from 'lucide-react';

const FAQ = () => {
  const [openItems, setOpenItems] = useState([]);

  const toggleItem = (id) => {
    setOpenItems(prev => 
      prev.includes(id) 
        ? prev.filter(item => item !== id)
        : [...prev, id]
    );
  };

  const faqs = [
    {
      id: 1,
      question: "Is Fokat Main gambling or betting?",
      answer: "No. Fokat Main is a skill-based gaming platform. Winners are determined by skill, speed, and accuracy — not chance. We provide gadget prizes only and do not offer cash rewards, making our contests distinct from gambling."
    },
    {
      id: 2,
      question: "How are winners decided?",
      answer: "Winners are determined based on a combination of correctness, response time, and adherence to the quiz rules. Only the first correct answer submitted without editing will be considered valid. Edited answers are not eligible even if corrected later."
    },
    {
      id: 3,
      question: "What if someone keeps winning repeatedly?",
      answer: "To ensure fair opportunity, a participant who wins three consecutive quizzes will be placed on a temporary cooldown and will not be eligible to win the next two quizzes. They are still welcome to participate for fun, but their entries will be excluded from prize consideration during the cooldown period."
    },
    {
      id: 4,
      question: "How do I receive my prize?",
      answer: "Winners will be contacted via their registered email or mobile number within 3 working days of quiz completion. Prizes are shipped free of cost to the winner's provided address within 7–10 working days. Tracking details will be shared after dispatch."
    },
    {
      id: 5,
      question: "What payment methods do you accept?",
      answer: "We accept secure payments through UPI, Credit/Debit Cards, and Net Banking, processed by our payment partner Cashfree Payments. All transactions are encrypted — we do not store card details on our servers."
    },
    {
      id: 6,
      question: "Can I get a refund?",
      answer: "Participation is final once confirmed and fees are generally non-refundable. Refunds may be considered for duplicate payments or platform errors if you contact support within 3 business days. Approved refunds are processed to the original payment method within 7–10 working days."
    },
    {
      id: 7,
      question: "How do I contact support?",
      answer: "For queries related to quizzes, prizes, or payments, email us at support@fokatmain.com or visit our Contact page. We aim to respond within 48 hours."
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-black via-gray-900 to-black py-20 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <header className="mb-12">
          <h1 className="text-4xl lg:text-5xl font-bold text-white mb-4">
            Frequently Asked Questions
          </h1>
          <p className="text-lg text-gray-400">
            Everything you need to know about Fokat Main — quizzes, prizes, policies and support.
          </p>
        </header>

        {/* FAQ Card */}
        <section className="bg-gray-900/50 backdrop-blur-sm rounded-2xl p-8 shadow-2xl border border-gray-800">
          {/* Intro */}
          <div className="mb-8 p-4 bg-gray-800/50 rounded-lg border border-gray-700">
            <p className="text-sm text-gray-300 leading-relaxed">
              Fokat Main is a skill-based gaming and quiz platform operated by{' '}
              <strong className="text-white">PLAYEDGE INNOVATIONS</strong>. Participants compete based on knowledge, 
              speed and accuracy. Winners receive gadget prizes only — no cash rewards.
            </p>
          </div>

          {/* Accordion */}
          <div className="space-y-2">
            {faqs.map((faq) => (
              <div 
                key={faq.id} 
                className="border-t border-gray-800 first:border-t-0"
              >
                <button
                  onClick={() => toggleItem(faq.id)}
                  className="w-full flex justify-between items-center py-5 text-left hover:text-yellow-400 transition-colors duration-200 group"
                  aria-expanded={openItems.includes(faq.id)}
                >
                  <h3 className="text-lg font-semibold text-white pr-4 group-hover:text-yellow-400 transition-colors">
                    {faq.question}
                  </h3>
                  <ChevronDown 
                    className={`w-5 h-5 text-gray-400 flex-shrink-0 transition-transform duration-300 ${
                      openItems.includes(faq.id) ? 'transform rotate-180 text-yellow-400' : ''
                    }`}
                  />
                </button>
                
                <div 
                  className={`overflow-hidden transition-all duration-300 ${
                    openItems.includes(faq.id) ? 'max-h-96 pb-5' : 'max-h-0'
                  }`}
                >
                  <p className="text-gray-400 leading-relaxed">
                    {faq.answer}
                  </p>
                </div>
              </div>
            ))}
          </div>

          {/* Links */}
          <div className="mt-8 flex flex-wrap gap-3">
            <a 
              href="/terms" 
              className="px-4 py-2 bg-gray-800 hover:bg-gray-700 border border-gray-700 rounded-lg text-sm text-white transition-colors duration-200"
            >
              Terms & Conditions
            </a>
            <a 
              href="/privacy" 
              className="px-4 py-2 bg-gray-800 hover:bg-gray-700 border border-gray-700 rounded-lg text-sm text-white transition-colors duration-200"
            >
              Privacy Policy
            </a>
            <a 
              href="/contact" 
              className="px-4 py-2 bg-gray-800 hover:bg-gray-700 border border-gray-700 rounded-lg text-sm text-white transition-colors duration-200"
            >
              Contact Support
            </a>
          </div>

          {/* Footer */}
          <footer className="mt-8 pt-6 border-t border-gray-800">
            <p className="text-xs text-gray-500">
              Last updated: November 2024. By participating you agree to our Terms & Conditions and Privacy Policy.
            </p>
          </footer>
        </section>
      </div>
    </div>
  );
};

export default FAQ;
